import { useState, useRef, useCallback, useEffect } from "react"
import { Send } from "lucide-react"
import { cn } from "@/lib/utils"

interface ConciergeInputProps {
  onSend: (text: string) => void
  disabled: boolean
  autoFocus?: boolean
}

export function ConciergeInput({ onSend, disabled, autoFocus }: ConciergeInputProps) {
  const [value, setValue] = useState("")
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const canSend = value.trim().length > 0 && !disabled

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 80)}px`
  }, [])

  useEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])

  // Auto-focus when panel opens
  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus()
    }
  }, [autoFocus])

  const handleSend = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return
    onSend(trimmed)
    setValue("")
  }, [value, disabled, onSend])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  return (
    <div className="p-4 border-t border-border bg-card">
      <div
        className={cn(
          "flex items-end gap-2",
          "bg-gray-50 border border-border rounded-lg p-2",
          "transition-colors duration-150",
          "focus-within:border-primary",
        )}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your properties..."
          rows={1}
          className="flex-1 bg-transparent border-none outline-none resize-none text-[13.5px] font-sans text-foreground min-h-[20px] max-h-[80px] leading-[1.4] placeholder:text-gray-400"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className={cn(
            "h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0",
            "transition-colors duration-150",
            canSend
              ? "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
              : "bg-gray-300 text-gray-500 cursor-not-allowed",
          )}
          aria-label="Send message"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
